import { useApp, useAuth } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import { Fragment } from 'react';
import { RcDl, RcLink, ShowDetailsSection, ShowStatus } from '../../../shared';
import { dateOnlyFormatter, trackingUrl } from '../../../transforms';

function isPlanStartDelayTodayOrLater(planStartDelay) {
  if (!planStartDelay) {
    return false;
  }

  const delayDate = new Date(planStartDelay);
  const today = new Date();
  const delayDateOnly = new Date(
    delayDate.getFullYear(),
    delayDate.getMonth(),
    delayDate.getDate(),
  );
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  return delayDateOnly >= todayOnly;
}

function OrderShowDetails({ order }) {
  const { isAdmin, isPartner } = useApp();
  const { currentUser } = useAuth();

  const isDelayValid =
    order.planStartDelay && isPlanStartDelayTodayOrLater(order.planStartDelay);

  return (
    <RcDl>
      {(isAdmin || (isPartner && !currentUser.scopeToLocation)) && (
        <ShowDetailsSection
          title={`${isAdmin ? 'Partner Location' : 'Location'}`}
        >
          <RcLink
            to={`${isPartner ? '/locations/' : '/partners/locations/'}${
              order?.partnerLocation?.id
            }`}
            className="text-sm"
          >
            {order?.partnerLocation?.name}
          </RcLink>
          <br />
          {order?.partnerLocation?.address?.city},&nbsp;
          {order?.partnerLocation?.address?.region}
        </ShowDetailsSection>
      )}
      <ShowDetailsSection title="Payment Status">
        <span>
          <ShowStatus state={order.paymentState} />
        </span>
      </ShowDetailsSection>
      {order.renewal && order.subscription && (
        <ShowDetailsSection title="Renewal For">
          {isAdmin ? (
            <RcLink
              to={`/customers/subscriptions/${order.subscription.id}`}
              className="text-sm"
            >
              {order.subscription.name} ({order.subscription.hexCode})
            </RcLink>
          ) : (
            <span>
              {order.subscription.name} ({order.subscription.hexCode})
            </span>
          )}
        </ShowDetailsSection>
      )}
      {order.awaitingSubscriptionAcknowledgment && (
        <ShowDetailsSection title="Subscription Terms">
          <span>
            <ShowStatus state="awaiting_customer" />
          </span>
          <p className="mt-1 text-black/60 text-xs">
            Production can&apos;t start until the customer signs in and
            confirms the plan terms.
            {order.subscriptionAcknowledgmentEmailSentAt && (
              <>
                {' '}
                Last emailed{' '}
                {new Date(
                  order.subscriptionAcknowledgmentEmailSentAt,
                ).toLocaleDateString()}
                .
              </>
            )}
          </p>
        </ShowDetailsSection>
      )}
      {isDelayValid && (
        <ShowDetailsSection title="Plan Start Delay">
          <time dateTime={order.planStartDelay}>
            {dateOnlyFormatter(order.planStartDelay)}
          </time>
        </ShowDetailsSection>
      )}
      <ShowDetailsSection title="EOT Order">
        <span>
          <ShowStatus state={order.eotOrder ? 'Yes' : 'No'} />
        </span>
      </ShowDetailsSection>
      {isAdmin && (
        <ShowDetailsSection title="Paid By Partner">
          <span>
            <ShowStatus state={order.paidByPartner ? 'Yes' : 'No'} />
          </span>
        </ShowDetailsSection>
      )}
      {isAdmin && (
        <ShowDetailsSection title="Ship To Clinic">
          <span>
            <ShowStatus state={order.sendToClinic ? 'Yes' : 'No'} />
          </span>
        </ShowDetailsSection>
      )}
      {isAdmin && (
        <ShowDetailsSection title="Delivery Method">
          <span>{order.deliveryMethod}</span>
        </ShowDetailsSection>
      )}

      <ShowDetailsSection title="Tracking Numbers">
        <span>
          {order.productionOrders.map((productionOrder) => (
            <Fragment key={productionOrder.id}>
              {productionOrder.productionOrderItems.some(
                (x) => x.trackingNumber,
              )
                ? productionOrder.productionOrderItems.map((item) => (
                    <span key={item.id} className="block text-sm">
                      <RcLink
                        href={trackingUrl(item.trackingNumber)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {item.trackingNumber}
                      </RcLink>
                      &nbsp;
                      {item.shippingCarrier
                        ? `(${item.shippingCarrier.name})`
                        : ''}
                    </span>
                  ))
                : null}
            </Fragment>
          ))}
        </span>
      </ShowDetailsSection>
    </RcDl>
  );
}

OrderShowDetails.propTypes = {
  order: PropTypes.object.isRequired,
};

export default OrderShowDetails;
